import React, { useState } from 'react';
import { styled, Button, Popover } from '@moleculeone/ui';

import { useKeyboard } from '@f/components/shared/compound-editor/hooks';

const Container = styled('div', {
  height: '30rem',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '$6',
});
const GroupTitle = styled('h4', {
  margin: '$5 0 $3',
  fontSize: '$3',
});
const Hotkey = styled('div', {
  display: 'flex',
  alignItems: 'center',
  fontSize: '$2',
  marginBottom: '$3',
});
const HotkeyAction = styled('div', {
  'width': '9rem',
  '& span': {
    color: '$slate9',
  },
});
const HotkeyDescription = styled('div', {
  color: '$slate12',
});
const Kbd = styled('kbd', {
  'display': 'inline-block',
  'backgroundColor': '$slate4',
  'borderRadius': '$small',
  'padding': '0 $3',
  'marginBottom': '$2',
  'color': '$slate12',
  '& + kbd': {
    marginLeft: '$2',
  },
});

export function HotKeys() {
  const [open, setOpen] = useState(false);

  useKeyboard({
    onKeyDown: event => {
      if (event.key === '?') {
        setOpen(true);
      }
    },
  });

  return (
    <Popover
      hasArrow
      trigger={<Button weight="minimal" color="slate" icon="question_mark" active={open} circle />}
      contentProps={{
        sideOffset: 5,
        side: 'left',
        css: {
          width: 'auto',
          padding: '0',
        },
      }}
      open={open}
      onOpenChange={open => {
        setOpen(open);
      }}
    >
      <Container>
        <Hotkey>
          <HotkeyAction>
            <Kbd>?</Kbd>
          </HotkeyAction>
          <HotkeyDescription>opens this window</HotkeyDescription>
        </Hotkey>

        <GroupTitle>Mouseover keyboard actions</GroupTitle>
        <Hotkey>
          <HotkeyAction>
            <Kbd>0</Kbd>
          </HotkeyAction>
          <HotkeyDescription>clear charge</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>+</Kbd> <span>or</span> <Kbd>=</Kbd>
          </HotkeyAction>
          <HotkeyDescription>increment charge</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>-</Kbd> <span>or</span> <Kbd>_</Kbd>
          </HotkeyAction>
          <HotkeyDescription>decrement charge</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>D</Kbd> <span>or</span> <Kbd>Del</Kbd>
          </HotkeyAction>
          <HotkeyDescription>remove atom/bond</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            {['B', 'C', 'N', 'O', 'F', 'P', 'S', 'I'].map((element, idx, all) => (
              <React.Fragment key={element}>
                {idx === Math.floor(all.length / 2) && <br />}
                <Kbd>{element}</Kbd>
              </React.Fragment>
            ))}
          </HotkeyAction>
          <HotkeyDescription>change element of an atom</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>L</Kbd>
          </HotkeyAction>
          <HotkeyDescription>change element to chlorine</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>R</Kbd>
          </HotkeyAction>
          <HotkeyDescription>change element to bromine</HotkeyDescription>
        </Hotkey>

        <GroupTitle>Mouse actions</GroupTitle>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Left btn</Kbd>
          </HotkeyAction>
          <HotkeyDescription>add or append carbon atom</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Left btn</Kbd> <span>+</span> <Kbd>Drag</Kbd>
          </HotkeyAction>
          <HotkeyDescription>add, append or connect atoms</HotkeyDescription>
        </Hotkey>

        <GroupTitle>Keyboard actions</GroupTitle>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Escape</Kbd>
          </HotkeyAction>
          <HotkeyDescription>clear selection / set default mode</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Space</Kbd>
          </HotkeyAction>
          <HotkeyDescription>redraw</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Ctrl</Kbd> <span>+</span> <Kbd>C</Kbd>
          </HotkeyAction>
          <HotkeyDescription>copy selected fragment to clipboard</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Ctrl</Kbd> <span>+</span> <Kbd>X</Kbd>
          </HotkeyAction>
          <HotkeyDescription>cut selected fragment to clipboard</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Ctrl</Kbd> <span>+</span> <Kbd>V</Kbd>
          </HotkeyAction>
          <HotkeyDescription>paste from clipboard</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Ctrl</Kbd> <span>+</span> <Kbd>Z</Kbd>
          </HotkeyAction>
          <HotkeyDescription>undo change</HotkeyDescription>
        </Hotkey>
        <Hotkey>
          <HotkeyAction>
            <Kbd>Ctrl</Kbd> <span>+</span> <Kbd>Y</Kbd> <span>or</span>
            <br />
            <Kbd>Shift</Kbd> <span>+</span> <Kbd>Ctrl</Kbd> <span>+</span> <Kbd>Z</Kbd>
          </HotkeyAction>
          <HotkeyDescription>redo change</HotkeyDescription>
        </Hotkey>
      </Container>
    </Popover>
  );
}
